'use client';

import { c as _c } from "react/compiler-runtime";
import './carousel.scss';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import Image from 'next/image';
import { ButtonHTMLAttributes, HTMLAttributes, KeyboardEvent, createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { MediaProps } from '@sola/schema/lib/schema.ts';
import { formatDuration } from '@sola/utils/video.ts';
import { Video } from '../../atoms/video/video.tsx';
type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};
type CarouselLazyLoadImageProps = {
  media: MediaProps | undefined;
  index?: number;
  isVideoThumbnail?: boolean;
  className?: string;
};
type CarouselLazyLoadVideoProps = {
  media: MediaProps;
  index?: number;
  className?: string;
  autoplay?: boolean;
};
type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  slidesLoaded: number[];
  selectedSlide: number;
} & CarouselProps;
type CarouselItemContextProps = {
  isHidden: boolean;
};
const CarouselContext = createContext<CarouselContextProps | null>(null);
const CarouselItemContext = createContext<CarouselItemContextProps | null>(null);
const useCarousel = () => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
};
const useCarouselItem = () => {
  const context = useContext(CarouselItemContext);
  if (!context) {
    throw new Error("useCarouselItem must be used within a <CarouselItem />");
  }
  return context;
};
const Carousel = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & CarouselProps>(({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}, ref) => {
  const [carouselRef, api] = useEmblaCarousel({
    ...opts,
    axis: orientation === 'horizontal' ? 'x' : 'y'
  }, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [slidesLoaded, setSlidesLoaded] = useState<number[]>([]);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const onSelect = useCallback(() => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    setSelectedSlide(api.selectedScrollSnap());
  }, [api]);
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      scrollPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      scrollNext();
    }
  }, [scrollPrev, scrollNext]);
  const updateSlidesLoaded = useCallback(() => {
    if (!api) {
      return;
    }
    setSlidesLoaded(prevSlidesLoaded => {
      if (prevSlidesLoaded.length === api.slideNodes().length) {
        api.off('slidesInView', updateSlidesLoaded);
        return prevSlidesLoaded;
      }
      const inView = api.slidesInView().filter(index => !prevSlidesLoaded.includes(index));
      if (inView.length === 0) {
        return prevSlidesLoaded;
      }
      // @TODO - possibly look into loading before and after slide too
      return prevSlidesLoaded.concat(inView);
    });
  }, [api]);
  const setFocusTrap = useCallback(() => {
    if (!api) {
      return;
    }
    const slidesInView = api?.slidesInView() ?? [];
    let slidesVisible;
    if (slidesInView.length > 1) {
      if (canScrollNext) {
        slidesVisible = slidesInView.slice(0, -1);
      } else {
        slidesVisible = slidesInView.slice(1);
      }
    } else {
      slidesVisible = [api?.selectedScrollSnap()];
    }
    api?.slideNodes().forEach((slide, index_0) => {
      const isVisible = slidesVisible.includes(index_0);
      const interactiveElements = slide.querySelectorAll('a, button, iframe');
      interactiveElements.forEach(element => {
        if (isVisible) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    });
  }, [api, canScrollNext]);
  useEffect(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);
  useEffect(() => {
    if (!api) {
      return;
    }
    onSelect();
    api.on('reInit', onSelect);
    api.on('select', onSelect);
    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);
  useEffect(() => {
    if (!api) {
      return;
    }
    updateSlidesLoaded();
    api.on('reInit', updateSlidesLoaded);
    api.on('slidesInView', updateSlidesLoaded);
    return () => {
      api?.off('slidesInView', updateSlidesLoaded);
    };
  }, [api, updateSlidesLoaded]);
  useEffect(() => {
    if (!api) {
      return;
    }
    setFocusTrap();
    api.on('reInit', setFocusTrap);
    api.on('slidesInView', setFocusTrap);
    return () => {
      api?.off('slidesInView', setFocusTrap);
    };
  }, [api, setFocusTrap]);
  return <CarouselContext.Provider value={useMemo(() => ({
    carouselRef,
    api,
    opts,
    orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
    slidesLoaded,
    selectedSlide
  }), [carouselRef, api, opts, orientation, scrollPrev, scrollNext, canScrollPrev, canScrollNext, slidesLoaded, selectedSlide])}>
        <div ref={ref} onKeyDownCapture={handleKeyDown} className={cn('relative', className)} role="region" aria-roledescription="carousel" {...props}>
          {children}
        </div>
      </CarouselContext.Provider>;
});
Carousel.displayName = 'Carousel';
const CarouselContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((t0, ref) => {
  const $ = _c(13);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    carouselRef,
    orientation
  } = useCarousel();
  const t1 = orientation === "horizontal" ? "flex-row" : "flex-col";
  let t2;
  if ($[3] !== className || $[4] !== t1) {
    t2 = cn("Carousel__container flex", t1, className);
    $[3] = className;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== ref || $[8] !== t2) {
    t3 = <div ref={ref} className={t2} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== carouselRef || $[11] !== t3) {
    t4 = <div ref={carouselRef} className="Carousel">{t3}</div>;
    $[10] = carouselRef;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
});
CarouselContent.displayName = 'CarouselContent';
const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & {
  index: number;
}>((t0, ref) => {
  const $ = _c(13);
  const {
    className,
    children,
    ...props
  } = t0;
  const {
    api
  } = useCarousel();
  const isHidden = !api?.slidesInView()?.includes(props.index as number);
  let t1;
  if ($[0] !== isHidden) {
    t1 = {
      isHidden
    };
    $[0] = isHidden;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1;
  const t3 = className ?? "Carousel__slide--default";
  let t4;
  if ($[2] !== t3) {
    t4 = cn("Carousel__slide", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== isHidden || $[6] !== props || $[7] !== ref || $[8] !== t4) {
    t5 = <div ref={ref} role="group" aria-roledescription="slide" aria-hidden={isHidden} className={t4} {...props}>{children}</div>;
    $[4] = children;
    $[5] = isHidden;
    $[6] = props;
    $[7] = ref;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <CarouselItemContext.Provider value={t2}>{t5}</CarouselItemContext.Provider>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
});
CarouselItem.displayName = 'CarouselItem';
const CarouselLazyLoadImage = t0 => {
  const $ = _c(22);
  const {
    index: t1,
    media,
    isVideoThumbnail: t2,
    className
  } = t0;
  const index = t1 === undefined ? 0 : t1;
  const isVideoThumbnail = t2 === undefined ? false : t2;
  const {
    isHidden
  } = useCarouselItem();
  const {
    slidesLoaded
  } = useCarousel();
  const [hasLoaded, setHasLoaded] = useState(!isHidden);
  let t3;
  let t4;
  if ($[0] !== index || $[1] !== slidesLoaded) {
    t3 = () => {
      if (slidesLoaded.indexOf(index) > -1) {
        setHasLoaded(true);
      }
    };
    t4 = [slidesLoaded, index];
    $[0] = index;
    $[1] = slidesLoaded;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setHasLoaded(true);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleImageLoad = t5;
  if (!media || !media?.image) {
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="w-full h-full" style={{
        backgroundImage: "url(/placeholder.svg)",
        backgroundSize: "20px"
      }} />;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    return t6;
  }
  const t6 = hasLoaded ? "has-loaded" : "";
  let t7;
  if ($[6] !== className || $[7] !== t6) {
    t7 = cn("Carousel__media Carousel__media--lazy-load", t6, className);
    $[6] = className;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isVideoThumbnail || $[10] !== media.video) {
    t8 = isVideoThumbnail && media.video && <div className="Carousel__video-thumb"><div className="Carousel__video-thumb__overlay" /><div className="Carousel__video-thumb__info">{media.video.duration && <div className="label-xl label-potent p-comfy-lg">{formatDuration(media.video.duration)}</div>}</div><div className="Carousel__video-thumb__play"><ArkIcon iconName="play" fill="currentColor" /></div></div>;
    $[9] = isVideoThumbnail;
    $[10] = media.video;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== hasLoaded) {
    t9 = !hasLoaded && <span className="Carousel__media__spinner" />;
    $[12] = hasLoaded;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== hasLoaded || $[15] !== media.image) {
    t10 = media.image && hasLoaded && <Image className="Carousel__img Carousel__img--lazy" onLoad={handleImageLoad} src={hasLoaded ? media.image.url : ""} alt={media.image.alt || ""} data-src={media.image.url} fill={true} style={{
      objectPosition: media.image.focus ? `${media.image.focus.x} ${media.image.focus.y}` : undefined
    }} />;
    $[14] = hasLoaded;
    $[15] = media.image;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t11 = <div className={t7}>{t8}{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
const CarouselLazyLoadVideo = t0 => {
  const $ = _c(17);
  const {
    index: t1,
    media,
    className,
    autoplay: t2
  } = t0;
  const index = t1 === undefined ? 0 : t1;
  const autoplay = t2 === undefined ? false : t2;
  const [allowPlaying, setAllowPlaying] = useState(false);
  const {
    selectedSlide,
    slidesLoaded
  } = useCarousel();
  const [hasLoaded, setHasLoaded] = useState(slidesLoaded.indexOf(index) > -1);
  let t3;
  let t4;
  if ($[0] !== index || $[1] !== selectedSlide) {
    t3 = () => {
      if (selectedSlide === index) {
        setAllowPlaying(true);
      } else {
        setAllowPlaying(false);
      }
    };
    t4 = [selectedSlide, index];
    $[0] = index;
    $[1] = selectedSlide;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[4] !== index || $[5] !== slidesLoaded) {
    t5 = () => {
      if (slidesLoaded.indexOf(index) > -1) {
        setHasLoaded(true);
      }
    };
    t6 = [slidesLoaded, index];
    $[4] = index;
    $[5] = slidesLoaded;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  if (!media.video) {
    return;
  }
  let t7;
  if ($[8] !== allowPlaying || $[9] !== autoplay || $[10] !== hasLoaded || $[11] !== media.image || $[12] !== media.video) {
    t7 = hasLoaded && <Video {...media.video} poster={media.image} allowPlaying={allowPlaying} autoplay={autoplay} renderJsonLd={false} />;
    $[8] = allowPlaying;
    $[9] = autoplay;
    $[10] = hasLoaded;
    $[11] = media.image;
    $[12] = media.video;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== className || $[15] !== t7) {
    t8 = <div className="CarouselVideo CarouselVideo--lazy flex"><div className={className}>{t7}</div></div>;
    $[14] = className;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};
const CarouselPrevious = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>((t0, ref) => {
  const $ = _c(18);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["skippy"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    scrollPrev,
    canScrollPrev
  } = useCarousel();
  const t2 = !canScrollPrev;
  let t3;
  if ($[4] !== className) {
    t3 = cn("Carousel__arrow", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArkIcon iconName="chevron-left" size="20" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("skippy:gallery.prev");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="sr-only"> {t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== props || $[12] !== ref || $[13] !== scrollPrev || $[14] !== t2 || $[15] !== t3 || $[16] !== t6) {
    t7 = <button ref={ref} type="button" disabled={t2} onClick={scrollPrev} className={t3} {...props}>{t4}{t6}</button>;
    $[11] = props;
    $[12] = ref;
    $[13] = scrollPrev;
    $[14] = t2;
    $[15] = t3;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
});
CarouselPrevious.displayName = 'CarouselPrevious';
const CarouselNext = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>((t0, ref) => {
  const $ = _c(17);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    t
  } = useTranslation();
  const {
    scrollNext,
    canScrollNext
  } = useCarousel();
  const t1 = !canScrollNext;
  let t2;
  if ($[3] !== className) {
    t2 = cn("Carousel__arrow", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon iconName="chevron-right" size="20" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("skippy:gallery.next");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="sr-only"> {t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props || $[11] !== ref || $[12] !== scrollNext || $[13] !== t1 || $[14] !== t2 || $[15] !== t5) {
    t6 = <button ref={ref} type="button" disabled={t1} onClick={scrollNext} className={t2} {...props}>{t3}{t5}</button>;
    $[10] = props;
    $[11] = ref;
    $[12] = scrollNext;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});
CarouselNext.displayName = 'CarouselNext';
const CarouselProgress = () => {
  const $ = _c(7);
  const {
    scrollNext,
    selectedSlide,
    api
  } = useCarousel();
  let t0;
  if ($[0] !== api) {
    t0 = api?.slideNodes().length;
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const progressText = `${selectedSlide + 1} / ${t0}`;
  let t1;
  if ($[2] !== scrollNext) {
    t1 = () => scrollNext();
    $[2] = scrollNext;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== progressText || $[5] !== t1) {
    t2 = <button type="button" className="Carousel__count-display" onClick={t1}>{progressText}</button>;
    $[4] = progressText;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselLazyLoadImage, CarouselLazyLoadVideo, CarouselPrevious, CarouselNext, CarouselProgress };